import {
  BrowserCultureInfo,
  DateFormat,
  DateFormatAttribute,
  enumKeys,
  Language,
  LanguageAttribute,
  TimeZoneAttribute,
  User,
  UserProfile,
} from '@bimone/bimtrack-core';
import { UserDto } from './types';
import { User as AuthenticatedUser } from 'oidc-client-ts';

const FRENCH_LANGUAGE_CODE_PREFIX = 'fr';
const FRENCH_LANGUAGE_NAME = 'Français';
const ENGLISH_LANGUAGE_NAME = 'English';
const ENGLISH_LANGUAGE_LOCALE = 'en-US';

enum DateFormatName {
  EN_US = 'M/D/YYYY H:MM TT',
  EN_CA = 'YYYY-MM-DD H:MM TT',
  FR_CA = 'DD/MM/YYYY HH:MM',
}

export class UserAssembler {
  private static readonly DEFAULT_DATE_FORMAT_ATTRIBUTE = new DateFormatAttribute({
    id: ENGLISH_LANGUAGE_LOCALE,
    name: DateFormatName.EN_US,
    format: DateFormat.EN_US,
  });

  public static toDomainFromAuth(dto: AuthenticatedUser): User {
    const locale = BrowserCultureInfo.getPreferredLanguage();

    return new User({
      globalId: dto.profile.name,
      email: dto.profile.email ?? '',
      profile: new UserProfile({
        firstName: dto.profile.given_name ?? '',
        lastName: dto.profile.family_name ?? '',
        avatarImage: dto.profile.picture ?? '',
        language: UserAssembler.buildLanguageAttribute(locale),
        dateFormat: UserAssembler.buildDateFormatAttributeFromBrowserCulture(locale),
        timeZone: UserAssembler.buildTimezoneAttribute(
          BrowserCultureInfo.getPreferredTimeZone(),
          BrowserCultureInfo.getPreferredTimeZone()
        ),
      }),
    });
  }

  public static toDomain(dto: UserDto, currentUser: User): User {
    const user = new User(currentUser);
    const userProfile = new UserProfile(currentUser.profile);

    return new User({
      ...user.toObject(),
      id: dto.Id,
      profile: new UserProfile({
        ...userProfile.toObject(),
        avatarImage: dto.AvatarUrl ?? '',
        language: UserAssembler.buildLanguageAttribute(dto.Language),
        dateFormat: UserAssembler.buildDateFormatAttribute(dto.DateFormat, dto.DateFormatCulture),
        timeZone: UserAssembler.buildTimezoneAttribute(dto.TimeZoneId, dto.TimeZoneNameIana),
      }),
    });
  }

  private static buildLanguageAttribute(locale?: string): LanguageAttribute {
    return new LanguageAttribute({
      id: UserAssembler.convertLocaleToLanguage(locale),
      name: UserAssembler.convertLocaleToName(locale),
    });
  }

  private static convertLocaleToLanguage(locale?: string): Language {
    if (locale && locale.toLowerCase().startsWith(FRENCH_LANGUAGE_CODE_PREFIX)) {
      return Language.FR;
    }

    return Language.EN;
  }

  private static convertLocaleToName(locale?: string): string {
    if (locale && locale.toLowerCase().startsWith(FRENCH_LANGUAGE_CODE_PREFIX)) {
      return FRENCH_LANGUAGE_NAME;
    }

    return ENGLISH_LANGUAGE_NAME;
  }

  private static buildDateFormatAttribute(dateFormatName: string, dateFormatCulture: string): DateFormatAttribute {
    return new DateFormatAttribute({
      id: dateFormatCulture,
      name: dateFormatName,
      format: UserAssembler.toDomainDateFormat(dateFormatName),
    });
  }

  private static buildDateFormatAttributeFromBrowserCulture(locale?: string): DateFormatAttribute {
    if (!locale) {
      return UserAssembler.DEFAULT_DATE_FORMAT_ATTRIBUTE;
    }
    for (const key of enumKeys(DateFormat)) {
      if (key.toUpperCase().startsWith(locale.toUpperCase())) {
        return new DateFormatAttribute({
          id: locale,
          name: DateFormatName[key],
          format: DateFormat[key],
        });
      }
    }
    return UserAssembler.DEFAULT_DATE_FORMAT_ATTRIBUTE;
  }

  private static toDomainDateFormat(dateFormatName: string): DateFormat {
    switch (dateFormatName) {
      case DateFormatName.EN_CA:
        return DateFormat.EN_CA;
      case DateFormatName.EN_US:
        return DateFormat.EN_US;
      case DateFormatName.FR_CA:
        return DateFormat.FR_CA;
      default:
        return DateFormat.EN_US;
    }
  }

  private static buildTimezoneAttribute(timeZone: string, timeZoneNameIana: string): TimeZoneAttribute {
    return new TimeZoneAttribute({
      id: timeZone,
      name: timeZone,
      nameIana: timeZoneNameIana,
    });
  }
}
